import DtoolsPdfQuotationPageHeader, {
  type DtoolsPdfQuotationHeaderInfo,
} from "@/components/sales/dtools/DtoolsPdfQuotationPageHeader";
import { BOM_PRINT_FONT, BOM_PRINT_H1_FONT_SIZE_PX, BOM_PRINT_LABEL } from "@/lib/bomPrintDesign";
import { PROPOSAL_ACCENT, PROPOSAL_TEXT, proposalMoney } from "@/lib/proposalTheme";
import type { DtoolsPortalPaymentTerm } from "@shared/dtoolsDisplaySettings";
import { withPaymentTermsDisplayFallback } from "@shared/proposalCustomerPaymentTerms";

export type DtoolsPdfSummarySystemRow = {
  key: string;
  label: string;
  amountFormatted: string;
  isAlternative?: boolean;
  isSelected?: boolean;
};

type Props = {
  header: DtoolsPdfQuotationHeaderInfo;
  /** Customer-facing proposal title (not BOM / not option name). */
  proposalTitle: string;
  systemRows: DtoolsPdfSummarySystemRow[];
  grandTotalFormatted: string;
  grandTotalLabel?: string;
  /** Option proposals: omit the Grand Total / Final Price row (customer chooses per group). */
  hideGrandTotal?: boolean;
  paymentTerms: DtoolsPortalPaymentTerm[];
  proposalStatus?: string | null;
  alternativeOptions?: boolean;
  /** When true, render only the repeating page chrome (header). */
  chromeOnly?: boolean;
  /** When true, omit the page header (body used under chrome). */
  bodyOnly?: boolean;
};

/** Concise Proposal Summary page — formal quotation language. */
export default function DtoolsPdfProposalSummarySection({
  header,
  proposalTitle,
  systemRows,
  grandTotalFormatted,
  grandTotalLabel = "Grand Total",
  hideGrandTotal = false,
  paymentTerms,
  proposalStatus,
  alternativeOptions = false,
  chromeOnly = false,
  bodyOnly = false,
}: Props) {
  const displayTerms = withPaymentTermsDisplayFallback(paymentTerms);
  const title = proposalTitle.trim() || "Proposal";

  if (chromeOnly) {
    return <DtoolsPdfQuotationPageHeader info={header} />;
  }

  return (
    <div className="space-y-4" style={{ fontFamily: BOM_PRINT_FONT, color: PROPOSAL_TEXT }}>
      {!bodyOnly ? <DtoolsPdfQuotationPageHeader info={header} /> : null}

      <h1
        className="font-bold uppercase tracking-[0.08em] text-[#1A2744] pdf-keep-together"
        data-testid="dtools-pdf-document-title"
        style={{
          fontSize: BOM_PRINT_H1_FONT_SIZE_PX,
          lineHeight: 1.25,
          marginTop: bodyOnly ? 0 : undefined,
          letterSpacing: "0.08em",
        }}
      >
        {title}
      </h1>

      <section className="space-y-2" data-testid="dtools-pdf-summary-systems">
        <h2
          className="font-semibold uppercase pdf-keep-together"
          style={{ fontSize: "9px", letterSpacing: "0.05em", color: BOM_PRINT_LABEL }}
        >
          Systems Included
        </h2>
        <table className="w-full border-collapse text-left" data-pdf-schedule-table="true">
          <thead>
            <tr style={{ borderBottom: "1px solid #1A2744" }}>
              <th
                className="font-semibold uppercase"
                style={{ fontSize: "9px", letterSpacing: "0.05em", color: BOM_PRINT_LABEL, padding: "6px 8px" }}
              >
                System / Option
              </th>
              {alternativeOptions ? (
                <th
                  className="font-semibold uppercase"
                  style={{ fontSize: "9px", letterSpacing: "0.05em", color: BOM_PRINT_LABEL, padding: "6px 8px" }}
                >
                  Notes
                </th>
              ) : null}
              <th className="py-1.5 text-right font-semibold uppercase tracking-[0.04em] text-[#6B7280]" style={{ fontSize: "9px" }}>
                Total (SGD)
              </th>
            </tr>
          </thead>
          <tbody data-pdf-schedule-body="true">
            {systemRows.map((row) => (
              <tr
                key={row.key}
                className="pdf-keep-together"
                data-pdf-pagination-unit="true"
                style={{ borderBottom: "1px solid #E5E7EB" }}
              >
                <td className="py-1.5 pr-3 font-medium text-[#1A2744]" style={{ fontSize: "10px" }}>{row.label}</td>
                {alternativeOptions ? (
                  <td className="py-1.5 pr-3 text-[#6B7280]" style={{ fontSize: "9px" }}>
                    {row.isSelected ? "Included" : row.isAlternative ? "Alternative" : "—"}
                  </td>
                ) : null}
                <td className="py-1.5 text-right text-[10px] font-semibold tabular-nums text-[#1A2744]">
                  {row.amountFormatted}
                </td>
              </tr>
            ))}
            {hideGrandTotal ? null : (
            <tr className="pdf-keep-together" data-pdf-pagination-unit="true" data-testid="dtools-pdf-summary-grand-total">
              <td
                className="py-2 pr-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#1A2744]"
                colSpan={alternativeOptions ? 2 : 1}
                style={{ borderTop: `2px solid ${PROPOSAL_ACCENT}` }}
              >
                {grandTotalLabel}
              </td>
              <td
                className="py-2 text-right text-[12px] font-bold tabular-nums text-[#1A2744]"
                style={{ borderTop: `2px solid ${PROPOSAL_ACCENT}` }}
              >
                {grandTotalFormatted}
              </td>
            </tr>
            )}
          </tbody>
        </table>
      </section>

      {displayTerms.length > 0 ? (
        <section className="space-y-1.5 pdf-keep-together" data-testid="dtools-pdf-summary-payment-terms">
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#6B7280]">Payment Terms</h2>
          <ul className="space-y-0.5 text-[10px] leading-relaxed text-[#374151]">
            {displayTerms.map((term, idx) => (
              <li key={`${term.stage}-${idx}`}>
                {term.fixedAmount != null && term.fixedAmount > 0
                  ? `${proposalMoney.format(term.fixedAmount)} ${term.stage}`
                  : `${Math.round(term.percentage)}% ${term.stage}`}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {proposalStatus?.trim() ? (
        <p className="text-[9px] uppercase tracking-[0.1em] text-[#6B7280] pdf-keep-together" data-testid="dtools-pdf-summary-status">
          Status: <span className="font-semibold text-[#1A2744]">{proposalStatus.trim()}</span>
        </p>
      ) : null}
    </div>
  );
}
